import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Animated, BackHandler, Easing, Linking, PanResponder, Platform, StatusBar, Text, useWindowDimensions, View } from "react-native";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { SessionProvider, useSession } from "../session/SessionProvider";
import { RootStackParamList } from "../navigation/types";
import { LoginScreen } from "../screens/LoginScreen";
import { HomeScreen } from "../screens/HomeScreen";
import { CatalogScreen } from "../screens/CatalogScreen";
import { ItemScreen } from "../screens/ItemScreen";
import { LibraryLandingScreen } from "../screens/LibraryLandingScreen";
import { StreamingServicesScreen } from "../screens/StreamingServicesScreen";
import { CollectionsScreen } from "../screens/CollectionsScreen";
import { PersonScreen } from "../screens/PersonScreen";
import { MusicHomeScreen } from "../screens/MusicHomeScreen";
import { MusicLibraryScreen } from "../screens/MusicLibraryScreen";
import { LiveTvScreen } from "../screens/LiveTvScreen";
import { SettingsScreen } from "../screens/SettingsScreen";
import { AdminScreen } from "../screens/AdminScreen";
import { PlayerScreen } from "../screens/PlayerScreen";
import { NotFoundScreen } from "../screens/NotFoundScreen";
import { MusicPlaybackProvider } from "../music/MusicPlaybackProvider";
import { AppErrorBoundary } from "../components/AppErrorBoundary";
import { LivingRoomInput } from "../components/LivingRoomInput";
import { TVFocusDebugOverlay } from "../components/TVFocusDebugOverlay";
import { PublicApkUpdateMonitor } from "../components/PublicApkUpdateMonitor";
import {
  pushNavigationPath,
  popNavigationPath,
  replaceNavigationPath,
  subscribeNavigationHistory,
} from "../navigation/navigationHistory";
import { getReferenceViewportScale, useReferenceWindowDimensions } from "../utils/referenceViewport";
import { RemotePlaybackProvider, useRemotePlayback } from "../session/RemotePlaybackProvider";
import { MatureTopNav } from "../components/MatureTopNav";
import { AppShellProvider } from "../components/AppShellContext";
import { isTelevisionRuntime } from "../utils/platformCapabilities";
import { colors, typography } from "../theme/tokens";
import { useNativePreferences } from "../storage/preferences";
import { FocusablePressable } from "../components/FocusablePressable";
import { setFocusRoute } from "../tvNavigation/registry";

type RouteName = keyof RootStackParamList;
type AppRoute = { name: RouteName; params?: RootStackParamList[RouteName] };

function decodeRoutePart(value: string | undefined) {
  if (!value) return "";
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function routeFromDeepLink(url: string | null): AppRoute | null {
  if (!url) return null;
  const routePath = url
    .split("#")[0]
    // App scheme links carry the route directly: jellyflix://play/<id>.
    .replace(/^jellyflix:\/\//i, "/")
    .replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, "");
  const [rawPathname, queryString = ""] = routePath.split("?");
  const pathname =
    rawPathname.length > 1 ? rawPathname.replace(/\/+$/, "") : rawPathname;
  const query = queryString
    .split("&")
    .reduce<Record<string, string>>((values, pair) => {
      const [key, value] = pair.split("=");
      if (key)
        values[decodeRoutePart(key)] = decodeRoutePart(
          value?.replace(/\+/g, " "),
        );
      return values;
    }, {});
  if (pathname === "/" || pathname === "") return { name: "Home" };
  if (pathname === "/library") return { name: "Library" };
  const serverLibrary = pathname.match(/^\/libraries\/([^/]+)/);
  if (serverLibrary && (query.type === "Movie" || query.type === "Series"))
    return {
      name: "ServerLibrary",
      params: {
        libraryId: decodeRoutePart(serverLibrary[1]),
        libraryName: query.name || "Library",
        itemType: query.type,
      },
    };
  if (pathname === "/login") return { name: "Login" };
  const genre = pathname.match(/^\/genre\/([^/]+)/);
  if (genre)
    return {
      name: "Genre",
      params: {
        genreId: decodeRoutePart(genre[1]),
        name: query.name || "Genre",
        ...(query.parentId ? { parentId: query.parentId } : {}),
        ...(query.type === "Movie" || query.type === "Series" ? { itemType: query.type } : {}),
        ...(query.libraryName ? { libraryName: query.libraryName } : {}),
      },
    };
  const streaming = pathname.match(/^\/streaming(?:\/([^/]+))?/);
  if (streaming)
    return {
      name: "Streaming",
      params: streaming[1]
        ? { serviceId: decodeRoutePart(streaming[1]) }
        : undefined,
    };
  const collections = pathname.match(/^\/collections(?:\/([^/]+))?/);
  if (collections)
    return {
      name: "Collections",
      params: collections[1]
        ? {
            collectionSlug: decodeRoutePart(collections[1]),
            ...(Number.isFinite(Number(query.page)) ? { page: Math.max(0, Number(query.page)) } : {}),
            ...(["SortName", "DateCreated", "PremiereDate", "CommunityRating", "Runtime"].includes(query.sortBy) ? { sortBy: query.sortBy as "SortName" } : {}),
            ...(["Ascending", "Descending"].includes(query.sortOrder) ? { sortOrder: query.sortOrder as "Ascending" } : {}),
          }
        : undefined,
    };
  if (pathname === "/search")
    return {
      name: "Search",
      params:
        query.q || query.tag
          ? {
              ...(query.q ? { query: query.q } : {}),
              ...(query.tag ? { tag: query.tag } : {}),
            }
          : undefined,
    };
  if (pathname === "/movies")
    return query.libraryId
      ? { name: "ServerLibrary", params: { itemType: "Movie", libraryId: query.libraryId, libraryName: query.libraryName || "Movies" } }
      : { name: "Library", params: { itemType: "Movie" } };
  if (pathname === "/tvshows")
    return query.libraryId
      ? { name: "ServerLibrary", params: { itemType: "Series", libraryId: query.libraryId, libraryName: query.libraryName || "TV Shows" } }
      : { name: "Library", params: { itemType: "Series" } };
  const musicGenre = pathname.match(/^\/music\/genre\/([^/]+)/);
  if (musicGenre)
    return {
      name: "Music",
      params: {
        view: "browse",
        genreId: decodeRoutePart(musicGenre[1]),
        genreName: query.name || "Genre",
      },
    };
  const musicDiscography = pathname.match(
    /^\/music\/artist\/([^/]+)\/discography/,
  );
  if (musicDiscography)
    return {
      name: "Music",
      params: {
        view: "discography",
        artistId: decodeRoutePart(musicDiscography[1]),
      },
    };
  if (pathname === "/music/liked")
    return { name: "Music", params: { view: "liked" } };
  if (pathname === "/music/browse")
    return { name: "Music", params: { view: "browse" } };
  if (pathname === "/music") return { name: "Music", params: { view: "home" } };
  if (pathname === "/livetv") return { name: "LiveTV" };
  if (pathname === "/settings") return { name: "Settings" };
  const admin = pathname.match(/^\/(?:admin|dashboard)(?:\/(.*))?$/);
  if (admin) return { name: "Admin", params: admin[1] ? { section: decodeRoutePart(admin[1]) } : undefined };
  const item = pathname.match(/^\/item\/([^/]+)/);
  if (item)
    return {
      name: "Item",
      params: {
        itemId: decodeRoutePart(item[1]),
        ...(query.seriesId ? { previewItem: { SeriesId: query.seriesId } } : {}),
      },
    };
  const player = pathname.match(/^\/play\/([^/]+)/);
  if (player)
    return {
      name: "Player",
      params: {
        itemId: decodeRoutePart(player[1]),
        ...(query.mediaSourceId
          ? { mediaSourceId: query.mediaSourceId }
          : {}),
        ...(query.startOver === "true" ? { startOver: true } : {}),
      },
    };
  const person = pathname.match(/^\/person\/([^/]+)/);
  if (person)
    return { name: "Person", params: { personId: decodeRoutePart(person[1]) } };
  return { name: "NotFound", params: { path: pathname } };
}

function routePath(route: AppRoute) {
  const encode = encodeURIComponent;
  const params = route.params as Record<string, string | boolean | undefined> | undefined;
  switch (route.name) {
    case "Home": return "/";
    case "Login": return "/login";
    case "Library": return params?.itemType === "Series" ? "/tvshows" : params?.itemType === "Movie" ? "/movies" : "/library";
    case "ServerLibrary": return `/libraries/${encode(String(params?.libraryId ?? ""))}?type=${encode(String(params?.itemType ?? "Movie"))}&name=${encode(String(params?.libraryName ?? "Library"))}`;
    case "Search": {
      const query = [["q", params?.query], ["tag", params?.tag]].filter((entry) => entry[1]).map(([key, value]) => `${key}=${encode(String(value))}`).join("&");
      return `/search${query ? `?${query}` : ""}`;
    }
    case "Genre": {
      const query = [["name", params?.name ?? "Genre"], ["parentId", params?.parentId], ["type", params?.itemType], ["libraryName", params?.libraryName]].filter((entry) => entry[1]).map(([key, value]) => `${key}=${encode(String(value))}`).join("&");
      return `/genre/${encode(String(params?.genreId ?? ""))}?${query}`;
    }
    case "Streaming": return params?.serviceId ? `/streaming/${encode(String(params.serviceId))}` : "/streaming";
    case "Collections": {
      if (!params?.collectionSlug) return "/collections";
      const query = [["page", params.page !== undefined ? String(params.page) : undefined], ["sortBy", params.sortBy], ["sortOrder", params.sortOrder]].filter((entry) => entry[1] !== undefined).map(([key, value]) => `${key}=${encode(String(value))}`).join("&");
      return `/collections/${encode(String(params.collectionSlug))}${query ? `?${query}` : ""}`;
    }
    case "Music":
      if (params?.view === "liked") return "/music/liked";
      if (params?.view === "browse" && params.genreId) return `/music/genre/${encode(String(params.genreId))}?name=${encode(String(params.genreName ?? "Genre"))}`;
      if (params?.view === "browse") return "/music/browse";
      if (params?.view === "discography" && params.artistId) return `/music/artist/${encode(String(params.artistId))}/discography`;
      return "/music";
    case "LiveTV": return "/livetv";
    case "Settings": return "/settings";
    case "Admin": return params?.section ? `/dashboard/${String(params.section).split("/").map(encode).join("/")}` : "/dashboard";
    case "Item": {
      const item = params?.previewItem as { Type?: string; Id?: string; SeriesId?: string } | undefined;
      const seriesId = item?.Type === "Series" ? item.Id : item?.SeriesId;
      return `/item/${encode(String(params?.itemId ?? ""))}${seriesId ? `?seriesId=${encode(String(seriesId))}` : ""}`;
    }
    case "Person": return `/person/${encode(String(params?.personId ?? ""))}`;
    case "Player": {
      const query = [["mediaSourceId", params?.mediaSourceId], ["startOver", params?.startOver ? "true" : undefined]].filter((entry) => entry[1]).map(([key, value]) => `${key}=${encode(String(value))}`).join("&");
      return `/play/${encode(String(params?.itemId ?? ""))}${query ? `?${query}` : ""}`;
    }
    case "NotFound": return String(params?.path || "/not-found");
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
    mutations: { retry: 0 },
  },
});

type RouteTransition = {
  from: AppRoute;
  to: AppRoute;
  direction: "forward" | "back";
};

function itemSeriesKey(route: AppRoute): string | undefined {
  if (route.name !== "Item") return undefined;
  const item = (route.params as RootStackParamList["Item"] | undefined)?.previewItem;
  if (!item) return undefined;
  return item.Type === "Series" ? item.Id : item.SeriesId;
}

function isSameSeriesDetail(from: AppRoute, to: AppRoute): boolean {
  if (from.name !== "Item" || to.name !== "Item") return false;
  if ((to.params as RootStackParamList["Item"] | undefined)?.transition === "none") return true;
  const fromSeries = itemSeriesKey(from);
  const toSeries = itemSeriesKey(to);
  return Boolean(fromSeries && toSeries && fromSeries === toSeries);
}

function RouteTransitionView({
  currentRoute,
  previousRoute,
  transition,
  canGoBack,
  reduceMotion,
  renderRoute,
  onComplete,
  onGestureBackComplete,
}: {
  currentRoute: AppRoute;
  previousRoute?: AppRoute;
  transition: RouteTransition | null;
  canGoBack: boolean;
  reduceMotion: boolean;
  renderRoute: (route: AppRoute) => React.ReactNode;
  onComplete: () => void;
  onGestureBackComplete: () => void;
}) {
  const { width } = useWindowDimensions();
  const progress = useRef(new Animated.Value(1)).current;
  const gestureTransition = useRef<RouteTransition | null>(null);
  const [gestureRenderTransition, setGestureRenderTransition] = useState<RouteTransition | null>(null);
  const [transitionInteractive, setTransitionInteractive] = useState(!transition);
  const [webTransitionSettled, setWebTransitionSettled] = useState(false);
  const webTransitionIdRef = useRef<string | null>(null);
  const gestureStartX = useRef(0);
  const mobileGestureEnabled = Platform.OS === "ios" || Platform.OS === "android";
  const televisionRuntime = isTelevisionRuntime();
  const viewportWidth = Math.max(width, 1);
  const activeTransition = gestureRenderTransition ?? transition;
  const webTransitionId = transition ? `${routePath(transition.from)}>${routePath(transition.to)}:${transition.direction}` : null;
  const detailTransition = transition?.from.name === "Item" && transition.to.name === "Item";
  // Detail pages are visually dense. Let the incoming page lead while the
  // outgoing page eases away only a short distance, so two large backdrops do
  // not create the feeling of two full screens colliding.
  const outgoingTravel = detailTransition ? viewportWidth * 0.34 : viewportWidth;
  // Keep the TV detail motion polished, but shorten the overlap window where
  // two large scenes are composited while the incoming screen mounts.
  const transitionDuration = reduceMotion ? 1 : detailTransition ? (televisionRuntime ? 280 : 360) : 290;
  const webAtRest = Platform.OS === "web" && activeTransition
    ? webTransitionIdRef.current === webTransitionId && webTransitionSettled
    : false;

  useEffect(() => {
    if (Platform.OS === "web") {
      if (!transition) {
        setWebTransitionSettled(false);
        setTransitionInteractive(true);
        return;
      }
      webTransitionIdRef.current = webTransitionId;
      setTransitionInteractive(false);
      setWebTransitionSettled(false);
      const startTimer = setTimeout(() => setWebTransitionSettled(true), reduceMotion ? 0 : 16);
      const finishTimer = setTimeout(() => {
        setWebTransitionSettled(true);
        setTransitionInteractive(true);
        onComplete();
      }, reduceMotion ? 1 : transitionDuration + 16);
      return () => {
        clearTimeout(startTimer);
        clearTimeout(finishTimer);
      };
    }
    if (!transition) {
      if (!gestureRenderTransition) {
        progress.setValue(1);
        setTransitionInteractive(true);
      }
      return;
    }
    setTransitionInteractive(false);
    progress.stopAnimation();
    progress.setValue(0);
    const animation = Animated.timing(progress, {
      toValue: 1,
      duration: transitionDuration,
      easing: Easing.bezier(0.12, 0.8, 0.24, 1),
      useNativeDriver: true,
    });
    animation.start(({ finished }) => {
      if (finished) {
        setTransitionInteractive(true);
        onComplete();
      }
    });
    return () => animation.stop();
  }, [gestureRenderTransition, onComplete, progress, reduceMotion, transition, transitionDuration, webTransitionId]);

  const panResponder = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponderCapture: (event) => {
      if (!mobileGestureEnabled || reduceMotion || !canGoBack || transition || !previousRoute) return false;
      gestureStartX.current = event.nativeEvent.pageX;
      return gestureStartX.current <= 28;
    },
    onMoveShouldSetPanResponderCapture: (_event, gestureState) => {
      if (!mobileGestureEnabled || reduceMotion || !canGoBack || transition || !previousRoute) return false;
      return gestureState.dx > 8 && Math.abs(gestureState.dx) > Math.abs(gestureState.dy) * 1.15;
    },
    onPanResponderGrant: () => {
      if (!previousRoute) return;
      const nextTransition: RouteTransition = { from: currentRoute, to: previousRoute, direction: "back" };
      gestureTransition.current = nextTransition;
      setGestureRenderTransition(nextTransition);
      setTransitionInteractive(false);
      progress.stopAnimation();
      progress.setValue(0);
    },
    onPanResponderMove: (_event, gestureState) => {
      if (!gestureTransition.current) return;
      progress.setValue(Math.max(0, Math.min(1, gestureState.dx / viewportWidth)));
    },
    onPanResponderRelease: (_event, gestureState) => {
      if (!gestureTransition.current) return;
      const complete = gestureState.dx > viewportWidth * 0.28 || gestureState.vx > 0.55;
      if (!complete) {
        Animated.spring(progress, { toValue: 0, useNativeDriver: true, speed: 20, bounciness: 0 }).start(() => {
          gestureTransition.current = null;
          setGestureRenderTransition(null);
          setTransitionInteractive(true);
        });
        return;
      }
      Animated.timing(progress, { toValue: 1, duration: 170, useNativeDriver: true }).start(({ finished }) => {
        if (!finished) return;
        gestureTransition.current = null;
        setGestureRenderTransition(null);
        setTransitionInteractive(true);
        onGestureBackComplete();
      });
    },
    onPanResponderTerminate: () => {
      if (!gestureTransition.current) return;
      Animated.spring(progress, { toValue: 0, useNativeDriver: true, speed: 20, bounciness: 0 }).start(() => {
        gestureTransition.current = null;
        setGestureRenderTransition(null);
        setTransitionInteractive(true);
      });
    },
  }), [canGoBack, currentRoute, mobileGestureEnabled, onGestureBackComplete, previousRoute, progress, reduceMotion, transition, viewportWidth]);

  const outgoingX = progress.interpolate({
    inputRange: [0, 1],
    // Keep the outgoing TV scene stationary and let the incoming scene carry
    // the motion; translating two full-screen surfaces increases compositor
    // work without adding meaningful polish on a 10-foot display.
    outputRange: televisionRuntime
      ? [0, 0]
      : activeTransition?.direction === "forward" ? [0, -outgoingTravel] : [0, outgoingTravel],
  });
  const incomingX = progress.interpolate({
    inputRange: [0, 1],
    outputRange: activeTransition?.direction === "forward" ? [viewportWidth, 0] : [-viewportWidth, 0],
  });
  const webDuration = transitionDuration;
  const webTransitionStyle = Platform.OS === "web" ? ({
    transitionProperty: "transform",
    transitionDuration: `${webAtRest ? webDuration : 0}ms`,
    transitionTimingFunction: "cubic-bezier(0.12, 0.8, 0.24, 1)",
    willChange: "transform",
  } as any) : undefined;
  const webOutgoingTranslate = activeTransition && webAtRest
    ? activeTransition.direction === "forward" ? -outgoingTravel : outgoingTravel
    : 0;
  const webIncomingTranslate = activeTransition && !webAtRest
    ? activeTransition.direction === "forward" ? viewportWidth : -viewportWidth
    : 0;
  return <View style={{ flex: 1, overflow: "hidden", backgroundColor: colors.background }} {...(mobileGestureEnabled ? panResponder.panHandlers : {})}>
    <Animated.View key="route-outgoing" pointerEvents="none" style={[{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0, opacity: activeTransition ? 1 : 0, backgroundColor: colors.background, backfaceVisibility: "hidden", transform: [{ translateX: Platform.OS === "web" ? webOutgoingTranslate : outgoingX }] }, webTransitionStyle]}>
      {activeTransition ? renderRoute(activeTransition.from) : null}
    </Animated.View>
    <Animated.View key="route-incoming" pointerEvents={transitionInteractive ? "auto" : "none"} style={[{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0, opacity: 1, backgroundColor: colors.background, backfaceVisibility: "hidden", transform: Platform.OS === "web" ? [{ translateX: webIncomingTranslate }] : (activeTransition ? [{ translateX: incomingX }] : undefined) }, webTransitionStyle]}>
      {renderRoute(activeTransition ? activeTransition.to : currentRoute)}
    </Animated.View>
  </View>;
}

function NativeRouter() {
  const preferences = useNativePreferences();
  const { width: referenceWidth } = useReferenceWindowDimensions();
  const { credentials, restoring } = useSession();
  const { selectedSessionId: remoteSessionId, playItem: playRemoteItem } = useRemotePlayback();
  const [routes, setRoutes] = useState<AppRoute[]>([{ name: "Login" }]);
  const routesRef = useRef(routes);
  const transitioningRef = useRef(false);
  const [transition, setTransition] = useState<RouteTransition | null>(null);
  const [deepLink, setDeepLink] = useState<{
    resolved: boolean;
    route: AppRoute | null;
  }>({ resolved: false, route: null });
  const current = routes[routes.length - 1];
  useEffect(() => {
    const params = current.params as { itemType?: 'Movie' | 'Series' } | undefined;
    const railId = current.name === 'Library'
      ? `shell.rail.${params?.itemType === 'Movie' ? 'movies' : 'tv-shows'}`
      : current.name === 'Music' ? 'shell.rail.music'
        : current.name === 'LiveTV' ? 'shell.rail.live-tv'
          : current.name === 'Home' ? 'shell.rail.home'
            : 'shell.rail.home';
    setFocusRoute(current.name, railId);
  }, [current.name, current.params]);
  routesRef.current = routes;
  const shouldAnimateRoute = useCallback((from: AppRoute, to: AppRoute) => (
    from.name !== "Login" && from.name !== "Player" && to.name !== "Login" && to.name !== "Player" && !isSameSeriesDetail(from, to)
  ), []);
  const routeDirection = useCallback((from: AppRoute, to: AppRoute): RouteTransition["direction"] => (
    to.name === "Home" && from.name !== "Home" ? "back" : "forward"
  ), []);
  const beginTransition = useCallback((from: AppRoute, to: AppRoute, direction: RouteTransition["direction"]) => {
    if (!shouldAnimateRoute(from, to)) {
      transitioningRef.current = false;
      setTransition(null);
      return;
    }
    transitioningRef.current = true;
    setTransition({ from, to, direction });
  }, [shouldAnimateRoute]);
  const completeTransition = useCallback(() => {
    transitioningRef.current = false;
    setTransition(null);
  }, []);
  useEffect(() => {
    let active = true;
    void Linking.getInitialURL()
      .then((url) => {
        if (!active) return;
        setDeepLink({ resolved: true, route: routeFromDeepLink(url) });
      })
      .catch(() => {
        if (active) setDeepLink({ resolved: true, route: null });
      });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => subscribeNavigationHistory((url, historyDirection) => {
    const next = routeFromDeepLink(url);
    if (!next) return;
    const existing = routesRef.current;
    const from = existing[existing.length - 1];
    if (routePath(from) === routePath(next)) return;
    beginTransition(from, next, next.name === "Home" && from.name !== "Home" ? "back" : historyDirection ?? routeDirection(from, next));
    const normalized = [next];
    routesRef.current = normalized;
    setRoutes(normalized);
  }), [beginTransition, routeDirection]);
  useEffect(() => {
    const subscription = Linking.addEventListener("url", ({ url }) => {
      const next = routeFromDeepLink(url);
      if (next) setRoutes([next]);
    });
    return () => subscription.remove();
  }, []);
  useEffect(() => {
    if (restoring || !deepLink.resolved) return;
    setRoutes((existing) => {
      const active = existing[existing.length - 1];
      if (credentials && active.name === "Login") {
        const next = deepLink.route && deepLink.route.name !== "Login" ? deepLink.route : { name: "Home" as const };
        replaceNavigationPath(routePath(next));
        const normalized = [next];
        routesRef.current = normalized;
        setTransition(null);
        transitioningRef.current = false;
        return normalized;
      }
      if (!credentials && active.name !== "Login") {
        replaceNavigationPath("/login");
        return [{ name: "Login" }];
      }
      return existing;
    });
  }, [credentials, deepLink, restoring]);

  const navigate = useCallback(
    (name: RouteName, params?: RootStackParamList[RouteName]) => {
      if (name === "Player" && remoteSessionId && params) {
        const playerParams = params as RootStackParamList["Player"];
        void playRemoteItem(playerParams.itemId, {
          startOver: playerParams.startOver,
          mediaSourceId: playerParams.mediaSourceId,
        }).catch(() => undefined);
        return;
      }
      const next = { name, params } as AppRoute;
      if (transitioningRef.current) return;
      const existing = routesRef.current;
      const from = existing[existing.length - 1];
      if (routePath(from) === routePath(next)) return;
      beginTransition(from, next, routeDirection(from, next));
      pushNavigationPath(routePath(next));
      const normalized = [...existing, next];
      routesRef.current = normalized;
      setRoutes(normalized);
    },
    [beginTransition, playRemoteItem, remoteSessionId, routeDirection],
  );
  const reset = useCallback(
    (state: {
      routes?: Array<{
        name: RouteName;
        params?: RootStackParamList[RouteName];
      }>;
    }) => {
      const next = state.routes?.length
        ? state.routes
        : [{ name: "Home" as const }];
      const normalized = next.map((route) => ({ name: route.name, params: route.params }));
      pushNavigationPath(routePath(normalized[normalized.length - 1] as AppRoute));
      routesRef.current = normalized;
      setTransition(null);
      transitioningRef.current = false;
      setRoutes(normalized);
    },
    [],
  );
  const goBack = useCallback(() => {
    if (popNavigationPath()) return;
    if (transitioningRef.current) return;
    const existing = routesRef.current;
    if (existing.length <= 1) return;
    const from = existing[existing.length - 1];
    const to = existing[existing.length - 2];
    beginTransition(from, to, "back");
    const normalized = existing.slice(0, -1);
    routesRef.current = normalized;
    setRoutes(normalized);
  }, [beginTransition]);
  const completeGestureBack = useCallback(() => {
    const existing = routesRef.current;
    if (existing.length <= 1) return;
    const normalized = existing.slice(0, -1);
    routesRef.current = normalized;
    setRoutes(normalized);
  }, []);
  useEffect(() => {
    if (Platform.OS === "web") return;
    const subscription = BackHandler.addEventListener("hardwareBackPress", () => {
      if (routes.length <= 1) {
        // On TV, consuming Back at the root keeps the user in the app
        // instead of dropping them to the system launcher.  This matches
        // standard streaming-app TV contracts (Netflix, Hulu, etc.).
        // On mobile, let the platform handle it normally.
        return true;
      }
      goBack();
      return true;
    });
    return () => subscription.remove();
  }, [goBack, routes.length]);
  const navigation = useMemo(
    () => ({
      navigate,
      push: navigate,
      replace: (name: RouteName, params?: RootStackParamList[RouteName]) => {
        if (name === "Player" && remoteSessionId && params) {
          const playerParams = params as RootStackParamList["Player"];
          void playRemoteItem(playerParams.itemId, {
            startOver: playerParams.startOver,
            mediaSourceId: playerParams.mediaSourceId,
          }).catch(() => undefined);
          return;
        }
        if (transitioningRef.current) return;
        const existing = routesRef.current;
        const next = { name, params } as AppRoute;
        const from = existing[existing.length - 1];
        beginTransition(from, next, routeDirection(from, next));
        replaceNavigationPath(routePath(next));
        const normalized = [...existing.slice(0, -1), next];
        routesRef.current = normalized;
        setRoutes(normalized);
      },
      reset,
      resetRoot: reset,
      goBack,
      canGoBack: () => routes.length > 1,
      isFocused: () => true,
      getState: () => ({
        stale: false,
        type: "stack",
        key: "jellyflix-native-router",
        index: routes.length - 1,
        routeNames: routes.map((route) => route.name),
        routes,
      }),
      setParams: (params: RootStackParamList[RouteName]) => {
        const existing = routesRef.current;
        const next = { ...existing[existing.length - 1], params } as AppRoute;
        replaceNavigationPath(routePath(next));
        const normalized = [...existing.slice(0, -1), next];
        routesRef.current = normalized;
        setRoutes(normalized);
      },
      dispatch: (action: {
        type?: string;
        payload?: {
          name?: RouteName;
          params?: RootStackParamList[RouteName];
          routes?: AppRoute[];
        };
      }) => {
        if (action.payload?.routes?.length)
          reset({ routes: action.payload.routes });
        else if (action.payload?.name)
          navigate(action.payload.name, action.payload.params);
      },
      addListener: () => () => undefined,
      removeListener: () => undefined,
      setOptions: () => undefined,
      getParent: () => undefined,
    }),
    [goBack, navigate, playRemoteItem, remoteSessionId, reset, routeDirection, routes],
  );


  // Keep route keys stable so screens survive back-navigation without
  // unmounting.  Previously the key included routes.length, which advanced
  // on every push/pop and forced React to destroy the HomeScreen tree,
  // restarting all queries and producing a ~10s black screen on return.
  const renderRoute = useCallback((target: AppRoute) => {
    const route = {
      key: `${target.name}:${routePath(target)}`,
      name: target.name,
      params: target.params,
    };
    return <View key={route.key} style={{ flex: 1, minWidth: 0 }}>{(() => { switch (target.name) {
    case "Login":
      return <LoginScreen />;
    case "Home":
      return (
        <HomeScreen
          key={route.key}
          navigation={navigation as never}
          route={route as never}
        />
      );
    case "Library":
      return (
        <LibraryLandingScreen
          navigation={navigation as never}
          route={route as never}
        />
      );
    case "ServerLibrary":
      return (
        <LibraryLandingScreen
          key={route.key}
          navigation={navigation as never}
          route={route as never}
        />
      );
    case "Item":
      return (
        <ItemScreen navigation={navigation as never} route={route as never} />
      );
    case "Player":
      return (
        <PlayerScreen
          key={route.key}
          navigation={navigation as never}
          route={route as never}
        />
      );
    case "Streaming":
      return (
        <StreamingServicesScreen
          navigation={navigation as never}
          route={route as never}
        />
      );
    case "Collections":
      return (
        <CollectionsScreen
          navigation={navigation as never}
          route={route as never}
        />
      );
    case "Person":
      return (
        <PersonScreen navigation={navigation as never} route={route as never} />
      );
    case "Music":
      return target.params &&
        "view" in target.params &&
        target.params.view &&
        target.params.view !== "home" ? (
        <MusicLibraryScreen
          navigation={navigation as never}
          route={route as never}
        />
      ) : (
        <MusicHomeScreen
          navigation={navigation as never}
          route={route as never}
        />
      );
    case "LiveTV":
      return (
        <LiveTvScreen navigation={navigation as never} route={route as never} />
      );
    case "Settings":
      return (
        <SettingsScreen
          navigation={navigation as never}
          route={route as never}
        />
      );
    case "Admin":
      return (
        <AdminScreen navigation={navigation as never} route={route as never} />
      );
    case "NotFound":
      return (
        <NotFoundScreen
          navigation={navigation as never}
          route={route as never}
        />
      );
    default:
      return (
        <CatalogScreen
          navigation={navigation as never}
          route={route as never}
        />
      );
  }})()}</View>;
  }, [navigation]);
  if (restoring || !deepLink.resolved) return null;
  const previousRoute = routes.length > 1 ? routes[routes.length - 2] : undefined;
  const shellActive = current.name !== "Login" && current.name !== "Player";
  const tvPreview = Platform.OS === 'web' && typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('tv') === '1';
  const sideRailActive = tvPreview || preferences.navigationMode === 'sidebar' || (preferences.navigationMode === 'auto' && isTelevisionRuntime());
  const desktopSearchRoute = current.name === "Search" && referenceWidth > 760;
  const searchRouteParams = current.name === "Search" ? current.params as RootStackParamList["Search"] : undefined;
  const transitionView = <RouteTransitionView currentRoute={current} previousRoute={previousRoute} transition={transition} canGoBack={routes.length > 1} reduceMotion={preferences.reduceMotion} renderRoute={renderRoute} onComplete={completeTransition} onGestureBackComplete={completeGestureBack} />;
  return shellActive ? (
    <View style={{ flex: 1, backgroundColor: "transparent" }}>
      <AppShellProvider sideRail={sideRailActive}>
        <View style={{ flex: 1, minWidth: 0 }}>{transitionView}</View>
      </AppShellProvider>
      {sideRailActive ? <>
        <FocusablePressable testID="tv-rail-control" controllerFocusable={false} accessibilityLabel="Jellyflix home" onPress={() => navigation.navigate("Home")} style={(focused, pressed, hovered) => [{ position: "absolute", top: 18, left: 16, zIndex: 100, paddingHorizontal: 4, paddingVertical: 4, borderRadius: 4 }, (focused || hovered) && { backgroundColor: "rgba(255,255,255,0.08)" }, pressed && { opacity: 0.7 }]}><Text style={[typography.brand, { fontSize: 20 }]}>JELLYFLIX</Text></FocusablePressable>
        <View style={{ position: (Platform.OS === "web" ? "fixed" : "absolute") as any, top: 0, bottom: 0, left: 0, width: 56, overflow: "visible", backgroundColor: "transparent", zIndex: 50 }}>
          <MatureTopNav navigation={navigation as never} active={current.name} activeItemType={(current.params as { itemType?: 'Movie' | 'Series' } | undefined)?.itemType} sideRail hideBrand />
        </View>
      </> : <MatureTopNav navigation={navigation as never} active={current.name} activeItemType={(current.params as { itemType?: 'Movie' | 'Series' } | undefined)?.itemType} overlay persistentSearch={desktopSearchRoute} searchValue={desktopSearchRoute ? searchRouteParams?.query ?? '' : undefined} onSearchValueChange={desktopSearchRoute ? (value) => navigation.setParams({ ...(searchRouteParams ?? {}), query: value }) : undefined} />}
    </View>
  ) : transitionView;
}

export default function App() {
  const { width, height } = useWindowDimensions();
  const referenceScale = getReferenceViewportScale();
  const referenceWidth = width * referenceScale;
  const referenceHeight = height * referenceScale;
  return (
    <View style={{ flex: 1, width, height, overflow: "hidden" }}>
      <LivingRoomInput />
      <PublicApkUpdateMonitor />
      <SafeAreaProvider style={{
        position: "absolute",
        left: -(referenceWidth - width) / 2,
        top: -(referenceHeight - height) / 2,
        width: referenceWidth,
        height: referenceHeight,
        transform: [{ scale: 1 / referenceScale }],
      }}>
        <QueryClientProvider client={queryClient}>
          <SessionProvider>
            <RemotePlaybackProvider>
              <MusicPlaybackProvider>
                <StatusBar
                  translucent
                  backgroundColor="transparent"
                  barStyle="light-content"
                />
                <AppErrorBoundary>
                  <NativeRouter />
                  <TVFocusDebugOverlay />
                </AppErrorBoundary>
              </MusicPlaybackProvider>
            </RemotePlaybackProvider>
          </SessionProvider>
        </QueryClientProvider>
      </SafeAreaProvider>
    </View>
  );
}
